import { dueState, dueTimeOf, parseYmd } from '@nedelnik/shared';

import type { DueState } from '@nedelnik/shared';
import type { Task } from '@nedelnik/shared';

/**
 * Срок: как он называется словами и откуда берётся «конец спринта».
 *
 * Само правило светофора живёт в `shared` (`dueState`) — оно общее с сервером и
 * с будущим iOS. Здесь только подписи: насколько именно просрочен — неважно,
 * важен сам факт (3.6.3).
 */

const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });

export type { DueState };

type DueParts = Pick<Task, 'due' | 'status'> & { due_time?: string | null };

/**
 * Время для подписи: «6:30», а не «06:30». Ведущий ноль нужен только сортировке
 * (строкой), а в строке задачи он читается как машинный.
 */
export function dueTimeLabel(task: Pick<DueParts, 'due' | 'due_time'>): string | null {
  const time = dueTimeOf(task);
  if (time === null) return null;
  return time.startsWith('0') ? time.slice(1) : time;
}

/**
 * Подпись срока.
 *
 * У закрытой задачи срок перестаёт выделяться (3.6.4), поэтому «просрочен» она
 * не показывает никогда — только дату: срок был, но больше ни на что не влияет.
 *
 * Время дописывается к дате и к «сегодня»/«завтра», но не к «просрочен»: у
 * просроченного важен факт, а не минуты (3.6.3).
 */
export function dueLabel(task: DueParts, today: string): string {
  if (task.due == null) return '';

  const state = dueState(task, today);
  if (state === 'overdue') return 'просрочен';

  const time = dueTimeLabel(task);
  const withTime = (day: string): string => (time === null ? day : `${day} ${time}`);
  if (state === 'soon') return withTime(task.due === today ? 'сегодня' : 'завтра');

  // Разбор даты обёрнут не из перестраховки: `Intl.format` от `Invalid Date`
  // бросает `RangeError`, а в React это уносит всё дерево — страница гаснет.
  // Показать срок как есть куда честнее, чем погасить экран из-за одной задачи
  // с испорченным полем.
  try {
    return withTime(DAY_MONTH.format(parseYmd(task.due)));
  } catch {
    return withTime(task.due);
  }
}

/** Тон подписи: далёкий срок — нейтрально, близкий — предупреждающе (3.6.3). */
export function dueTone(task: Pick<Task, 'due' | 'status'>, today: string): DueState {
  return dueState(task, today);
}
